import React from "react";
import { Link } from "react-router-dom";
import BoxedTitle from "@/components/BoxedTitle";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/063b9062-b1d7-4f16-8d31-e465e039ed51/images/406df7120a6f61d5a26734ee207025f1cc4c38f0ee6cb74ba0fb777e8ec5e47f.png";

export default function Home() {
  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-20 pb-28 lg:pt-28 lg:pb-40">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-7">
              <div className="vbb-overline vbb-rise" data-testid="hero-eyebrow">
                <span className="vbb-dot" />
                Business Development &amp; Product Incubation
              </div>

              {/* Boxed wordmark with a small "open" notch on top */}
              <div className="mt-8 relative">
                <div className="border border-[#0a0a0a] p-6 sm:p-10 relative vbb-rise vbb-rise-2">
                  <h1
                    className="font-display uppercase leading-[0.88] tracking-tighter text-[18vw] sm:text-[15vw] lg:text-[9.5rem] xl:text-[11rem]"
                    data-testid="hero-title"
                  >
                    Very
                    <br />
                    Big Box
                  </h1>

                  {/* Small opening — ~1em from the left, extends to about the end of "Very".
                      Font-size matches the heading so em scales naturally. */}
                  <span
                    aria-hidden="true"
                    className="absolute -top-[2px] h-[5px] sm:h-[6px] bg-[#fafafa] pointer-events-none leading-none text-[18vw] sm:text-[15vw] lg:text-[9.5rem] xl:text-[11rem]"
                    style={{ left: "0.25em", width: "3em" }}
                    data-testid="hero-box-opening"
                  />
                </div>
              </div>

              <p
                className="mt-12 max-w-xl text-[17px] sm:text-[19px] leading-relaxed text-[#2a2a2a] vbb-rise vbb-rise-3"
                data-testid="hero-subtitle"
              >
                Very Big Box LLC is a small, deliberate business development
                studio. We incubate ideas, ship products, and partner with
                founders who want to build something genuinely worth building.
              </p>

              <div
                className="mt-10 flex flex-wrap items-center gap-4 vbb-rise vbb-rise-4"
                data-testid="hero-cta-row"
              >
                <Link
                  to="/services"
                  className="vbb-btn"
                  data-testid="hero-services-cta"
                >
                  What we do <span aria-hidden>→</span>
                </Link>
                <Link
                  to="/contact"
                  className="vbb-btn vbb-btn-ghost"
                  data-testid="hero-contact-cta"
                >
                  Start a project
                </Link>
              </div>
            </div>

            {/* Right side: floating boxes image */}
            <div className="lg:col-span-5">
              <div
                className="relative aspect-[4/5] border border-[#0a0a0a] overflow-hidden vbb-grain vbb-rise vbb-rise-3"
                data-testid="hero-image-frame"
              >
                <img
                  src={HERO_IMG}
                  alt="Floating glass boxes"
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 p-4 vbb-border-t bg-[#fafafa]">
                  <div className="font-mono-tiny text-[#0a0a0a]">
                    Fig. 01 — Boxes, real &amp; imagined.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Ticker */}
        <div
          className="vbb-border-t vbb-border-b overflow-hidden bg-[#0a0a0a] text-[#fafafa]"
          data-testid="hero-marquee"
        >
          <div className="vbb-marquee-track flex whitespace-nowrap py-4">
            {Array.from({ length: 2 }).map((_, copy) => (
              <div key={`marquee-copy-${copy}`} className="flex items-center">
                {[
                  "Product Incubation",
                  "AI-Driven Builds",
                  "Founder Partnership",
                  "Outside the Box",
                  "Inside one of our own",
                  "Built with Emergent.sh",
                ].map((t) => (
                  <span
                    key={`copy-${copy}-${t}`}
                    className="font-display uppercase text-[20px] sm:text-[26px] tracking-tight mx-10 inline-flex items-center"
                  >
                    <span className="w-2 h-2 bg-[#e34234] mr-6" />
                    {t}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* THE MOTTO TEASER */}
      <section
        className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 lg:py-32"
        data-testid="home-motto-section"
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="vbb-overline">
              <span className="vbb-dot" />
              Our Philosophy
            </div>
          </div>
          <div className="lg:col-span-8">
            <p className="font-display text-3xl sm:text-5xl leading-[1.05] tracking-tight">
              “My thinking is{" "}
              <span className="text-[#e34234]">Outside the Box</span>. But on
              the other hand, we all live in a box of our own construction —
              built from our knowledge, beliefs, and experience.”
            </p>
            <div className="mt-8 flex items-center gap-4">
              <span className="font-mono-tiny">— Gary Bickford, Founder</span>
              <Link
                to="/about"
                className="vbb-link font-mono-tiny"
                data-testid="home-about-link"
              >
                Read more →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CAPABILITIES STRIP */}
      <section
        className="vbb-border-t vbb-border-b"
        data-testid="home-capabilities"
      >
        <div className="mx-auto max-w-[1400px] grid grid-cols-1 md:grid-cols-3">
          {[
            {
              n: "01",
              t: "Strategy",
              d: "Validate the box you’re in. Decide which walls to keep, which to break.",
            },
            {
              n: "02",
              t: "Product",
              d: "From sketch to shipped — design, engineering, and integration with modern AI tools.",
            },
            {
              n: "03",
              t: "Partnership",
              d: "We co-build with founders. We don’t outsource you out of your own product.",
            },
          ].map((c, i) => (
            <div
              key={c.n}
              className={`p-10 lg:p-14 ${
                i !== 2 ? "md:vbb-border-r" : ""
              } ${i !== 0 ? "vbb-border-t md:!border-t-0" : ""}`}
              data-testid={`capability-${c.n}`}
            >
              <div className="font-mono-tiny text-[#6b6b6b]">{c.n}</div>
              <h3 className="font-display text-2xl mt-4 uppercase tracking-tight pl-[0.35em]">
                <BoxedTitle size="sm">{c.t}</BoxedTitle>
              </h3>
              <p className="mt-4 text-[15px] leading-relaxed text-[#3a3a3a]">
                {c.d}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED PROJECT */}
      <section
        className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 lg:py-32"
        data-testid="home-featured-project"
      >
        <div className="flex items-end justify-between gap-6 mb-10">
          <div>
            <div className="vbb-overline">
              <span className="vbb-dot" />
              Featured Project
            </div>
            <h2 className="font-display text-4xl sm:text-6xl mt-4 uppercase tracking-tighter pl-[0.35em]">
              <BoxedTitle size="md">All American</BoxedTitle> Dog Club
            </h2>
          </div>
          <Link
            to="/portfolio"
            className="font-mono-tiny vbb-link hidden sm:inline"
            data-testid="home-portfolio-link"
          >
            View all →
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 vbb-portfolio">
          <div className="lg:col-span-7 border border-[#0a0a0a] overflow-hidden aspect-[16/10]">
            <img
              src="https://images.unsplash.com/photo-1504430225224-21a649ac91bc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxlbGVnYW50JTIwZG9nJTIwZ29sZGVuJTIwcmV0cmlldmVyJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzc5MzIxNjEyfDA&ixlib=rb-4.1.0&q=85"
              alt="All American Dog Club"
              className="w-full h-full object-cover vbb-portfolio-img"
            />
          </div>
          <div className="lg:col-span-5 flex flex-col justify-between">
            <div>
              <div className="font-mono-tiny text-[#6b6b6b]">
                Status — In Development
              </div>
              <p className="mt-6 text-[17px] leading-relaxed text-[#2a2a2a]">
                A community and platform celebrating the American dog —
                purebred, mutt, working, lounging, and everything in between.
                Built end-to-end inside VBB with AI-assisted product workflows.
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href="https://allamericandog.club"
                target="_blank"
                rel="noopener noreferrer"
                className="vbb-btn"
                data-testid="home-aadc-visit-btn"
              >
                Visit project &#8599;
              </a>
              <Link
                to="/portfolio"
                className="vbb-btn vbb-btn-ghost"
                data-testid="home-portfolio-btn"
              >
                More work
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section
        className="bg-[#0a0a0a] text-[#fafafa]"
        data-testid="home-cta-strip"
      >
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="font-mono-tiny text-[#9a9a9a]">
              <span className="vbb-dot" />
              For Founders &amp; Operators
            </div>
            <h2 className="font-display text-4xl sm:text-6xl lg:text-7xl mt-6 uppercase tracking-tighter leading-[0.92]">
              Have an idea
              <br />
              that doesn’t fit
              <br />
              <span className="text-[#e34234]">any obvious box?</span>
            </h2>
          </div>
          <div className="lg:col-span-4">
            <p className="text-[17px] leading-relaxed text-[#cfcfcf]">
              VBB partners with a small number of projects a year. If you’re
              building something with teeth, send us a note.
            </p>
            <Link
              to="/contact"
              className="vbb-btn mt-8 bg-[#fafafa] text-[#0a0a0a] border-[#fafafa] hover:!bg-[#e34234] hover:!text-[#fafafa] hover:!border-[#e34234]"
              data-testid="home-cta-contact-btn"
            >
              Get in touch <span aria-hidden>→</span>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
